import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { bandErledigt, bandMelden } from "./bandZustand";
import { useToast } from "./toastKontext";

/** D-089 und F33 (W51): Sichern aus einem Blatt. `laeuft` gilt ab dem Aufruf im Klick, also vor dem naechsten Tastendruck
 *  (`isPending` kaeme einen Takt spaeter, W47f R1 A1); das Blatt bekommt `gesperrt={laeuft}`. Die Blaetter rufen
 *  `sichern(x.mutateAsync(vars, { onSuccess, onError }))`; die Ablehnung faengt der Haken. Scheitert die Anfrage, wenn
 *  das Blatt zu oder die Ansicht weg ist (die Rueckrufe von TanStack laufen dann nicht mehr), meldet das Band den Fehler
 *  mit Weg zurueck; gelingt sie, nimmt sie ein Band derselben Ansicht weg und meldet, ist die Ansicht weg, kurz
 *  „Gesichert.“ (F1 = A mit Zusatz, Nachtrag W51; die Rueckmeldung des Blatts laeuft dann nicht mehr). */
/** W82 Web-5f (BS30, K47, D-603): so lange wartet ein Blatt auf die Antwort, das `sichern` mit einer Funktion aufruft. Danach
 *  bricht die Anfrage ab (das Versprechen scheitert wie ohne Netz, die Blätter zeigen „Keine Verbindung. Bitte später noch
 *  einmal.“ aus `fehlerText`), `laeuft` wird falsch und „Abbrechen“ und „Sichern“ sind wieder frei. */
export const SICHERN_GRENZE_MS = 10_000;

/** W82 Web-5h1 (K47): dieselbe Grenze für Mutationen ohne Blatt (Knopf, Rückfrage, Karte; Tabelle in Punkt 6 des Auftrags).
 *  Nach SICHERN_GRENZE_MS ohne Antwort bricht die Anfrage ab und das Versprechen scheitert wie ohne Netz („Keine Verbindung.
 *  Bitte später noch einmal.“ aus `fehlerText`). `aussen`: ein Signal des Aufrufers (etwa `useSichern`) bricht ebenfalls ab. Der
 *  Zeitgeber (nicht AbortSignal.timeout) folgt auch einer vorgestellten Uhr im Test. W82 Beta-W (D-603, entscheide C5): `grenzeMs`
 *  nur für begründete Ausnahmen (Rückmeldung mit Bildschirmfoto 30 s), alle anderen Aufrufer bleiben bei SICHERN_GRENZE_MS. */
export function mitGrenze<T>(lauf: (signal: AbortSignal) => Promise<T>, aussen?: AbortSignal, grenzeMs: number = SICHERN_GRENZE_MS): Promise<T> {
  const abbruch = new AbortController();
  const weiter = () => abbruch.abort(aussen?.reason);
  if (aussen?.aborted) weiter();
  else aussen?.addEventListener("abort", weiter, { once: true });
  const uhr = setTimeout(() => abbruch.abort(new DOMException("Keine Antwort", "TimeoutError")), grenzeMs);
  return lauf(abbruch.signal).finally(() => {
    clearTimeout(uhr);
    aussen?.removeEventListener("abort", weiter);
  });
}

/** W82 Web-11a W11A-4 (R1 Web-10c H-3): wie `mitGrenze`, scheitert nach Ablauf der Grenze aber immer mit `TimeoutError`, gleich wie
 *  der Browser den Abbruch der Anfrage meldet (abgefangen oder gedrosselt kommt dort auch ein TypeError an); so erkennt der
 *  Aufrufer „keine Antwort“ sicher. */
export function mitGrenzeErkannt<T>(lauf: (signal: AbortSignal) => Promise<T>, aussen?: AbortSignal, grenzeMs: number = SICHERN_GRENZE_MS): Promise<T> {
  let um = false;
  return mitGrenze(
    (signal) => {
      signal.addEventListener("abort", () => (um = signal.reason instanceof DOMException && signal.reason.name === "TimeoutError"), { once: true });
      return lauf(signal);
    },
    aussen,
    grenzeMs,
  ).catch((e: unknown) => {
    throw um ? new DOMException("Keine Antwort", "TimeoutError") : e;
  });
}

/** Ein Lauf: das Versprechen der Mutation, oder (BS30) eine Funktion, die es mit dem Abbruchsignal der Zeitgrenze startet. */
export type SichernLauf = Promise<unknown> | ((signal: AbortSignal) => Promise<unknown>);

export function useSichern(offen: boolean): { laeuft: boolean; sichern: (lauf: SichernLauf, grenzeMs?: number) => Promise<unknown> } {
  const [laeuft, setLaeuft] = useState(false);
  const ort = useLocation();
  const toast = useToast();
  const da = useRef(false);
  const offenJetzt = useRef(offen);
  // Zahl der laufenden Anfragen: frei erst, wenn die letzte fertig ist
  const zahl = useRef(0);
  useEffect(() => {
    offenJetzt.current = offen;
  });
  useEffect(() => {
    da.current = true;
    return () => {
      da.current = false;
    };
  }, []);
  const sichern = useCallback(
    (lauf: SichernLauf, grenzeMs: number = SICHERN_GRENZE_MS) => {
      // der Pfad beim Start: dorthin fuehrt der Weg zurueck, auch wenn die Ansicht inzwischen eine andere ist
      const zu = ort.pathname + ort.search;
      zahl.current += 1;
      setLaeuft(true);
      // BS30: die Zeitgrenze gehört dem Sichern, nicht der einzelnen Mutation; der Zeitgeber (nicht AbortSignal.timeout)
      // folgt auch einer vorgestellten Uhr im Test
      // W82 Web-5h1: das beobachtete Versprechen geht zurück; wer danach daran hängt, läuft nach den Haken hier (Meldung nach dem
      // Verlassen, `AbsenderZusammenlegenSheet.tsx`)
      const versprechen: Promise<unknown> = typeof lauf === "function" ? mitGrenze(lauf, undefined, grenzeMs) : lauf;
      versprechen
        .then(
          () => {
            bandErledigt(zu);
            // ausgehaengt heisst: die Rueckrufe von TanStack (Meldung, Schliessen) laufen nicht mehr
            if (!da.current) toast("Gesichert.");
          },
          () => {
            if (!da.current || !offenJetzt.current) bandMelden(zu);
          },
        )
        .finally(() => {
          zahl.current -= 1;
          if (da.current && zahl.current === 0) setLaeuft(false);
        });
      return versprechen;
    },
    [ort.pathname, ort.search, toast],
  );
  return { laeuft, sichern };
}
